/**
 * Security utility functions with improved reliability and less aggressive measures
 */

/**
 * Prevents the website from being loaded in iframes (clickjacking protection)
 * with improved error handling
 */
export const preventFraming = () => {
  try {
    if (window.self !== window.top) {
      // Only attempt redirect if we can access top
      if (window.top && window.top.location) {
        window.top.location.href = window.self.location.href;
      }
    }
  } catch (e) {
    console.error('Frame protection error (this is expected in iframes):', e);
    // We don't need to take action here, just log the error
  }
};

/**
 * Disables right-click context menu in production only
 * Less aggressive implementation
 */
export const disableContextMenu = () => {
  if (import.meta.env.PROD && !window.location.hostname.includes('localhost')) {
    // We'll exclude certain elements where right-click is essential
    document.addEventListener('contextmenu', (e) => {
      // Get the element that was right-clicked
      const target = e.target as HTMLElement;
      
      // Allow right-click on form elements and links
      const allowRightClick = 
        target.tagName === 'INPUT' || 
        target.tagName === 'TEXTAREA' || 
        target.tagName === 'A' ||
        target.tagName === 'SELECT';
      
      if (!allowRightClick) {
        e.preventDefault();
        return false;
      }
      return true;
    });
  }
};

/**
 * Disables developer tools shortcuts in production only
 * Less aggressive implementation
 */
export const disableDevTools = () => {
  if (import.meta.env.PROD && !window.location.hostname.includes('localhost')) {
    // Only prevent F12, allow other dev shortcuts for better accessibility
    document.addEventListener('keydown', (e) => {
      if (e.key === 'F12') {
        e.preventDefault();
      }
    });
  }
};

/**
 * Make console less useful but keep essential functionality
 * Only in production and not on localhost
 */
export const limitConsole = () => {
  if (import.meta.env.PROD && !window.location.hostname.includes('localhost')) {
    try {
      // Keep original console methods
      const originalConsole = { ...console };
      
      // Keep error and warn for essential debugging
      console.error = originalConsole.error;
      console.warn = originalConsole.warn;
      
      // Replace non-essential methods with limited versions
      const limitedLogger = (level: string) => (...args: any[]) => {
        if (args[0] && typeof args[0] === 'string' && 
            (args[0].includes('error') || args[0].includes('fail') || args[0].includes('critical'))) {
          // Let critical messages through
          originalConsole[level](...args);
        }
      };
      
      // Replace non-critical methods
      console.log = limitedLogger('log');
      console.debug = limitedLogger('debug');
      console.info = limitedLogger('info');
    } catch (e) {
      // If this fails, don't break the application
      console.error('Error configuring console security:', e);
    }
  }
};

/**
 * Initialize all security measures with better error handling
 * and progressive enhancement
 */
export const initSecurity = () => {
  try {
    console.log('Security module initializing');
    
    // Apply core security measures immediately
    preventFraming();
    
    // Apply more restrictive measures with a delay
    // to ensure they don't interfere with rendering
    setTimeout(() => {
      try {
        if (import.meta.env.PROD && !window.location.hostname.includes('localhost')) {
          disableContextMenu();
          disableDevTools();
          limitConsole();
          console.log('All security measures initialized');
        }
      } catch (error) {
        // Log but don't break the app
        console.error('Error in delayed security initialization:', error);
      }
    }, 3000);
  } catch (e) {
    console.error('Security initialization error:', e);
    // Continue application execution even if security fails
  }
};
